// Creates the Router and its context. The session is not in it: the _authenticated boundary
// asks the API (the reseller profile) instead, so the API stays the one judge.
import { QueryClient } from "@tanstack/react-query";
import { createRouter, type RouterHistory } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

export type RouterContext = { queryClient: QueryClient };

/** A test passes a memory history; the browser uses the default. */
export function createAppRouter({ history }: { history?: RouterHistory } = {}) {
  const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });
  return createRouter({
    routeTree,
    ...(history ? { history } : {}),
    context: { queryClient } satisfies RouterContext,
    defaultPreload: "intent",
    // TanStack Query owns caching; the router always asks it.
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
  interface StaticDataRouteOption {
    /** The route's name in the top bar's breadcrumbs (components/breadcrumbs.tsx): a message. */
    crumb?: () => string;
    /** The route has no page of its own (a group like /staff/domains): its crumb is not a link. */
    crumbUnlinked?: true;
  }
}
